import { Component, OnInit, OnDestroy, Input, AfterViewInit, ViewChild } from '@angular/core';

import { Observable, Subscription, Subject } from 'rxjs';
import { map, tap, debounceTime } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';

import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { IncludeOrExclude } from '@Common/Dialogs/services/data-select-dialog.service.interface';
import { JournodexSubjectModel, JournodexSubjectParentModel } from '@Common/Journodex/models/journodex.subject.model';
import { JournodexSubjectsService } from '@Common/Journodex/services/journodex-subjects.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ScrollPanelComponent } from '@Common/Basic/components/ScrollPanel/scroll-panel.component';

enum SubjectTab {
    general,
    regional
}


interface ChipGroup {
    id: number;
    heading: string;
    chips: Chip[];
}

interface Chip {
    id: number;
    tooltip: string;
    label: string;
}

@Component({
    selector: 'agpr-subject-dialog',
    templateUrl: './subject-dialog.component.html',
    styleUrls: ['./subject-dialog.component.scss'],
    standalone: false
}) export class SubjectDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() includeExclude: IncludeOrExclude;
    /** Array of parent subjects with selected subjects in the children property */
    @Input() selectedSubjects: JournodexSubjectParentModel[] = [];
    @Input() scrollToParentId: number | null = null;
    @Input() set initialFilterString(query: string) {
        if (query) {
            this.filterString = query;
        }
    }

    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;

    overlayContainer: Element;
    selectedTabIndex: SubjectTab = SubjectTab.general;
    filterString: string = '';

    suggestions: TreeNode[] = [];
    selectedSuggestions: TreeNode<JournodexSubjectModel>[] = [];

    generalOptions: TreeNode<JournodexSubjectParentModel>[] = [];
    regionalOptions: TreeNode<JournodexSubjectParentModel>[] = [];

    /** Are child selections */
    selectedGeneral: TreeNode<JournodexSubjectModel>[] = [];
    selectedRegional: TreeNode<JournodexSubjectModel>[] = [];

    chipGroups: ChipGroup[] = [];

    emptySubjects: boolean = false;

    private subscriptions: Subscription[] = [];
    private filterChanged$ = new Subject<void>();

    readonly id = 'subjectDialog';
    readonly buttonType = ButtonTypesEnum;
    readonly subjectTabType = SubjectTab;
    readonly regionalParentSubjectIds = [10121, 12131, 12227, 9757, 10329, 11437, 11761];
    readonly messageSeverityEnum = messageSeverityEnum;

    constructor(
        coreDialogService: CoreDialogService,
        protected journodexSubjectsService: JournodexSubjectsService,
        protected localizationService: LocalizationService
    ) {
        super(coreDialogService, 'SubjectDialogComponent');
    }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.journodexSubjectsService.getAllSubjects().subscribe(options => {
                const allSubjects = this.convertDataToTreeNodes(options);

                this.generalOptions = allSubjects.filter(x => !this.regionalParentSubjectIds.includes(x.data.id));
                this.regionalOptions = allSubjects.filter(x => this.regionalParentSubjectIds.includes(x.data.id));

                this.mapSelectedSubjectToNodes(this.generalOptions, this.regionalOptions);
                this.selectionChange();

                if (this.filterString) {
                    this.filterItems();
                }
            }),
            this.filterChanged$.pipe(debounceTime(300)).subscribe(() => {
                this.filterItems();
            })
        );
    }

    ngAfterViewInit() {
        this.scrollToParentSubject();
    }


    tabChange(index: number) {
        this.selectedTabIndex = index;
        this.selectedSuggestions = [];
    }

    filterChange() {
        this.filterChanged$.next();
    }

    apply() {
        // it appears we do a getByIds call to get the parents and send them
        // the formGroup expects JournodexSubjectParentModel[]
        const selectedSubectIds = [...this.selectedGeneral, ...this.selectedRegional].map(x => x.data.id);

        // This can be done if we assume getByIds() (which uses getAllSubjects()) will return a value using of(), which has a
        // null scheduler meaning it runs synchronously
        this.journodexSubjectsService.getByIds(selectedSubectIds).subscribe({
            next: (matchingParents) => {
                const uniqueParents = matchingParents.filter((parent, index, self) => index === self.findIndex((p) => p.id === parent.id));
                this.closeDialog(uniqueParents);
            },
            error: () => {
                this.closeDialog(null);
            }
        });
    }

    resetAll() {
        this.selectedGeneral = [];
        this.selectedRegional = [];
        this.chipGroups = [];
    }


    /** recalculate chipGroups based on the current subject selection */
    selectionChange() {
        this.chipGroups = [];

        for (const subject of [...this.selectedGeneral, ...this.selectedRegional]) {
            if (!subject.parent) {
                continue;
            }
            const existingChipGroup = this.chipGroups.find(x => x.id === subject.parent?.data.id);
            if (existingChipGroup) {
                existingChipGroup.chips.push({
                    id: subject.data.id,
                    label: subject.label,
                    tooltip: subject.data.tooltip,
                });
            } else {
                this.chipGroups.push({
                    id: subject.parent.data.id,
                    heading: subject.parent.data.name,
                    chips: [{
                        id: subject.data.id,
                        label: subject.label,
                        tooltip: subject.data.tooltip,
                    }]
                });
            }
        }

        this.chipGroups.forEach(x => x.chips.sort((a, b) => a.label.localeCompare(b.label)));
        this.chipGroups.sort((a, b) => a.heading.localeCompare(b.heading));
    }


    /**
     * Remove corresponding subject from main trees
     */
    suggestionUnselect(suggestion) {
        const suggestionSubject = suggestion.node;

        if (this.selectedTabIndex === SubjectTab.general) {
            const generalIndex = this.selectedGeneral.findIndex(child => child.data.id === suggestionSubject.data.id);
            if (generalIndex >= 0) {
                this.selectedGeneral.splice(generalIndex, 1);
            }
        } else {
            const regionalIndex = this.selectedRegional.findIndex(child => child.data.id === suggestionSubject.data.id);
            if (regionalIndex >= 0) {
                this.selectedRegional.splice(regionalIndex, 1);
            }
        }
        this.selectionChange();
    }


    suggestionSelect(suggestion) {
        const suggestionSubject = suggestion.node;

        if (this.selectedTabIndex === SubjectTab.general) {
            for (const parent of this.generalOptions) {
                const generalChild = parent.children.find(child => child.data.id === suggestionSubject.data.id);
                if (generalChild) {
                    this.selectedGeneral.push(generalChild as unknown as TreeNode<JournodexSubjectModel>);
                    break;
                }
            }
        } else {
            for (const parent of this.regionalOptions) {
                const regionalChild = parent.children.find(child => child.data.id === suggestionSubject.data.id);
                if (regionalChild) {
                    this.selectedRegional.push(regionalChild as unknown as TreeNode<JournodexSubjectModel>);
                    break;
                }
            }
        }
        this.selectionChange();
    }


    removeChip(chip: Chip) {
        this.selectedGeneral = this.selectedGeneral.filter(x => x.data.id !== chip.id);
        this.selectedRegional = this.selectedRegional.filter(x => x.data.id !== chip.id);
        this.selectionChange();
    }

    /**
     * Filter the tree list and populate the suggested list
     */
    private filterItems() {
        const query = this.filterString.toLocaleLowerCase();
        const treeNodes = (this.selectedTabIndex === SubjectTab.general)
            ? this.generalOptions : this.regionalOptions;
        const filteredChildNodes: TreeNode[] = [];
        this.suggestions = [];
        this.emptySubjects = true;

        if (this.filterString.length === 0) {
            treeNodes.forEach(parent => {
                parent.styleClass = '';
                parent.expanded = false;
                parent.children?.forEach(child => {
                    child.styleClass = '';
                    child.expanded = false;
                });
            });
            this.emptySubjects = false;
            return;
        } else if (this.filterString.length <= 2) {
            this.emptySubjects = false;
            return;
        }

        treeNodes.forEach(parent => {
            parent.styleClass = 'hide';
            let childFound = false;

            parent.children?.forEach(child => {
                if (child.label.toLocaleLowerCase().includes(query)) {
                    child.styleClass = '';
                    childFound = true;
                    filteredChildNodes.push(child);
                } else {
                    child.styleClass = 'hide';
                }
            });

            if (childFound || parent.label.toLocaleLowerCase().includes(query)) {
                parent.expanded = true;
                parent.styleClass = '';
                this.emptySubjects = false;
            }
        });

        this.journodexSubjectsService.quicksearchrelated(query).pipe(
            debounceTime(200),
            map(subjects => this.convertDataToSuggestionNodes(subjects)),
            map(suggestionNodes => this.filterDuplicatedSuggestions(suggestionNodes, filteredChildNodes)),
            tap(suggestionNodes => {
                // update selection state of suggestions
                this.selectedSuggestions = suggestionNodes.filter(node => {
                    if (this.selectedTabIndex === SubjectTab.general) {
                        this.selectedGeneral.find(selectedNode => selectedNode.data.id === node.data.id);
                    } else {
                        this.selectedRegional.find(selectedNode => selectedNode.data.id === node.data.id);
                    }
                });
            })
        ).subscribe(suggestions => {
            this.suggestions = suggestions;
        });
    }

    private convertDataToTreeNodes(parents: JournodexSubjectParentModel[]): TreeNode[] {
        const convertedArray: TreeNode[] = [];
        for (const parent of parents) {
            const tooltip = this.localizationService.transform('Discover.targeting.definition.subjects.' + parent.id);
            const label = this.localizationService.transform(parent.name);
            // Checking if tooltip exists, if not just passing the label as the tooltip
            parent.tooltip = (tooltip === 'Discover.targeting.definition.subjects.' + parent.id) ? label : tooltip;

            const treeNodeParent = {
                label: label,
                data: parent,
                type: 'Subject',
                children: [],
                selectable: false
            };
            parent.children.forEach(child => {
                let name = child.name;
                if ((child.name.length === 0) && (child.names?.[0]?.value)) {
                    name = child.names[0].value;
                }
                treeNodeParent.children.push({
                    label: this.localizationService.transform(name),
                    data: {
                        ...child,
                        type: parent.id.toString()
                    },
                    type: 'Subject',
                    parent: {
                        ...parent,
                        data: parent
                    }
                });
            });
            convertedArray.push(treeNodeParent);
        }

        return convertedArray.sort((a, b) => a.label.localeCompare(b.label));
    }


    private convertDataToSuggestionNodes(parents: JournodexSubjectParentModel[]): TreeNode[] {
        const suggestionNodes = [];
        parents.forEach(parent => {
            // Parent-level nodes are not returned, except in one (seemingly anomalous) instance
            // Thus, we are ignoring all parent-level subjects right now. See [NEP-7574]
            parent.children.forEach(child => {
                suggestionNodes.push({
                    label: child.name,
                    data: child,
                    type: 'Subject',
                });
            });
        });

        return suggestionNodes.sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Filter out any suggestions already shown in filtered section
     */
    private filterDuplicatedSuggestions(
        suggestions: TreeNode[],
        filteredChildNodes: TreeNode[],
    ): TreeNode[] {

        const nodesToRemove: TreeNode[] = [];
        suggestions.forEach(suggestion => {
            const duplicate = filteredChildNodes.find(child => child.data.id === suggestion.data.id);
            if (duplicate && duplicate.styleClass === '') {
                nodesToRemove.push(suggestion);
            }
        });

        return suggestions.filter(suggestion => !nodesToRemove.includes(suggestion));
    }

    /**
     * Map the selectedSubjects children to the proper `selectedRegional` and `selectedGeneral` TreeNode arrays
    */
    private mapSelectedSubjectToNodes(
        generalOptions: TreeNode[],
        regionalOptions: TreeNode[]
    ) {
        this.selectedSubjects.forEach(parent => {
            const matchingGeneral = generalOptions.find(treeNode => treeNode.data.id === parent.id);
            const matchingRegion = regionalOptions.find(treeNode => treeNode.data.id === parent.id);

            if (matchingGeneral) {
                const matchingChildren = matchingGeneral.children
                    .filter(child => {
                        return !!parent.children.find(givenParent => givenParent.id === child.data.id);
                    });
                this.selectedGeneral.push(...matchingChildren);
            }

            if (matchingRegion) {
                const matchingChildren = matchingRegion.children
                    .filter(child => {
                        return !!parent.children.find(givenParent => givenParent.id === child.data.id);
                    });
                this.selectedRegional.push(...matchingChildren);
            }
        });
    }

    private scrollToParentSubject() {
        if (!this.scrollToParentId) {
            return;
        }

        const generalIndex = this.generalOptions.findIndex(node => node.data.id === this.scrollToParentId);
        const regionalIndex = this.regionalOptions.findIndex(node => node.data.id === this.scrollToParentId);

        if (generalIndex >= 0) {
            requestAnimationFrame(() => {
                this.tabChange(SubjectTab.general);
            });
            const scrollNode = this.scrollPanel.sp.el.nativeElement.querySelectorAll('.p-tree-node-content')[generalIndex];
            this.scrollPanel.scrollIntoViewNative(scrollNode);

            const scrolledToNode = this.generalOptions.find(node => node.data.id === this.scrollToParentId);
            if (scrolledToNode) {
                scrolledToNode.expanded = true;
            }
        } else if (regionalIndex >= 0) {
            requestAnimationFrame(() => {
                this.tabChange(SubjectTab.regional);
            });
            const scrollNode = this.scrollPanel.sp.el.nativeElement.querySelectorAll('.p-tree-node-content')[regionalIndex];
            this.scrollPanel.scrollIntoViewNative(scrollNode);

            const scrolledToNode = this.regionalOptions.find(node => node.data.id === this.scrollToParentId);
            if (scrolledToNode) {
                scrolledToNode.expanded = true;
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
